// Hooks de datos de Promos venta. Sin base URL ninguna query se dispara.
import {
    useInfiniteQuery,
    useMutation,
    useQueries,
    useQueryClient,
    type InfiniteData,
} from '@tanstack/react-query';
import { useState } from 'react';
import { shouldRetryPromotionsQuery } from '../lib/promotionsFormat';
import { mergeReferralLists } from '../lib/referralKpis';
import { isPromotionsConfigured, promotionsClient } from './promotions.client';
import type {
    ListSalePromotionsResponse,
    ReferralChannel,
    SalePromotionKind,
    SalePromotionKpis,
    SalePromotionListItem,
    SalePromotionLogEntry,
    SalePromotionTransition,
} from './promotionsContract';

export const PROMOTIONS_QUERY_KEY = ['retail-admin', 'promotions'] as const;
const KEY = PROMOTIONS_QUERY_KEY;
const PAGE_SIZE = 50;

export function usePromotionsConfigured(): boolean {
    return isPromotionsConfigured();
}

export interface SalePromotionsState {
    items: SalePromotionListItem[];
    /** Los de la primera página: el back los calcula sobre todas las promos. */
    kpis: SalePromotionKpis | undefined;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isSuccess: boolean;
    error: unknown;
    fetchNextPage: () => Promise<unknown>;
    refetch: () => Promise<unknown>;
}

/**
 * Vigentes (sin `status`) o solo las archivadas; paginado con `nextToken`. Sin `kind` el back excluye
 * las de referido; `kind: 'REFERRAL'` con `channel` trae las de esa pestaña.
 */
export function useSalePromotions({
    archived,
    enabled,
    kind,
    channel,
    retailerId,
}: {
    archived: boolean;
    enabled: boolean;
    kind?: SalePromotionKind;
    channel?: ReferralChannel;
    retailerId?: string;
}): SalePromotionsState {
    const query = useInfiniteQuery({
        queryKey: salePromotionsListKey({ archived, kind, channel, retailerId }),
        queryFn: ({ pageParam }) =>
            promotionsClient.listSalePromotions({
                status: archived ? 'ARCHIVED' : undefined,
                ...(kind ? { kind } : {}),
                ...(channel ? { channel } : {}),
                ...(retailerId ? { retailerId } : {}),
                limit: PAGE_SIZE,
                nextToken: pageParam,
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        enabled: enabled && isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
        staleTime: 30_000,
    });
    return {
        items: query.data?.pages.flatMap((page) => page.items) ?? [],
        kpis: query.data?.pages[0]?.kpis,
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isSuccess: query.isSuccess,
        error: query.error,
        fetchNextPage: () => query.fetchNextPage(),
        refetch: () => query.refetch(),
    };
}

/** La llave de una lista; la comparten la lista de una cadena y la de varias, para no duplicar caché. */
export function salePromotionsListKey(params: {
    archived: boolean;
    kind?: SalePromotionKind;
    channel?: ReferralChannel;
    retailerId?: string;
}) {
    const { archived, kind, channel, retailerId } = params;
    return [...KEY, 'list', { archived, kind, channel, retailerId }] as const;
}

export interface RetailerReferralList extends SalePromotionsState {
    retailerId: string;
}

/** Las promos de referido de varias cadenas; ver `useSalePromotionsByRetailer`. */
export function useReferralPromotionsByRetailer({
    channel,
    retailerIds,
    enabled,
}: {
    channel: ReferralChannel;
    retailerIds: readonly string[];
    enabled: boolean;
}): RetailerReferralList[] {
    return useSalePromotionsByRetailer({
        archived: false,
        kind: 'REFERRAL',
        channel,
        retailerIds,
        enabled,
    });
}

/**
 * Las promos de varias cadenas, cada una con su lectura y su caché. Guarda las páginas con la misma
 * forma que `useSalePromotions`, así ambas leen y escriben la misma entrada.
 */
export function useSalePromotionsByRetailer({
    archived,
    kind,
    channel,
    retailerIds,
    enabled,
}: {
    archived: boolean;
    kind?: SalePromotionKind;
    channel?: ReferralChannel;
    retailerIds: readonly string[];
    enabled: boolean;
}): RetailerReferralList[] {
    const qc = useQueryClient();
    const keyOf = (retailerId: string) =>
        salePromotionsListKey({ archived, kind, channel, retailerId });
    const fetchPage = (retailerId: string, nextToken?: string) =>
        promotionsClient.listSalePromotions({
            status: archived ? 'ARCHIVED' : undefined,
            ...(kind ? { kind } : {}),
            ...(channel ? { channel } : {}),
            retailerId,
            limit: PAGE_SIZE,
            nextToken,
        });
    const results = useQueries({
        queries: retailerIds.map((retailerId) => ({
            queryKey: salePromotionsListKey({ archived, kind, channel, retailerId }),
            queryFn: async (): Promise<
                InfiniteData<ListSalePromotionsResponse, string | undefined>
            > => ({
                pages: [
                    await promotionsClient.listSalePromotions({
                        status: archived ? 'ARCHIVED' : undefined,
                        ...(kind ? { kind } : {}),
                        ...(channel ? { channel } : {}),
                        retailerId,
                        limit: PAGE_SIZE,
                    }),
                ],
                pageParams: [undefined],
            }),
            enabled: enabled && isPromotionsConfigured(),
            retry: shouldRetryPromotionsQuery,
            staleTime: 30_000,
        })),
    });
    return retailerIds.map((retailerId, index) => {
        const query = results[index];
        const pages = query.data?.pages ?? [];
        const nextToken = pages.at(-1)?.nextToken ?? undefined;
        return {
            retailerId,
            items: pages.flatMap((page) => page.items),
            kpis: pages[0]?.kpis,
            hasNextPage: nextToken !== undefined,
            isFetchingNextPage: false,
            isSuccess: query.isSuccess,
            error: query.error,
            fetchNextPage: async () => {
                if (nextToken === undefined) return;
                const page = await fetchPage(retailerId, nextToken);
                qc.setQueryData<InfiniteData<ListSalePromotionsResponse, string | undefined>>(
                    keyOf(retailerId),
                    (old) =>
                        old
                            ? {
                                  pages: [...old.pages, page],
                                  pageParams: [...old.pageParams, nextToken],
                              }
                            : old,
                );
            },
            refetch: () => query.refetch(),
        };
    });
}

/** Las listas de todas las cadenas como una sola, con el primer error que haya. */
export function useCombinedRetailerLists(
    byRetailer: readonly RetailerReferralList[],
    retailersLoading: boolean,
): SalePromotionsState {
    const [fetchingMore, setFetchingMore] = useState(false);
    const merged = mergeReferralLists(byRetailer);
    const settled = !retailersLoading && byRetailer.every((one) => one.isSuccess || one.error);
    return {
        items: merged.items,
        kpis: merged.kpis,
        isSuccess: settled,
        error: byRetailer.find((one) => one.error)?.error ?? null,
        hasNextPage: merged.hasNextPage,
        isFetchingNextPage: fetchingMore,
        fetchNextPage: async () => {
            setFetchingMore(true);
            try {
                await Promise.all(
                    byRetailer.filter((one) => one.hasNextPage).map((one) => one.fetchNextPage()),
                );
            } finally {
                setFetchingMore(false);
            }
        },
        refetch: () => Promise.all(byRetailer.map((one) => one.refetch())),
    };
}

export interface SalePromotionLogState {
    entries: SalePromotionLogEntry[];
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    isPending: boolean;
    error: unknown;
    fetchNextPage: () => Promise<unknown>;
    refetch: () => Promise<unknown>;
}

/** Historial global, o de una promo si llega `promotionId`; `retailerId` lo pide el admin de tenant. */
export function useSalePromotionLog({
    promotionId,
    retailerId,
}: {
    promotionId?: string;
    retailerId?: string;
}): SalePromotionLogState {
    const query = useInfiniteQuery({
        queryKey: [...KEY, 'log', promotionId ?? 'all', retailerId ?? 'own'],
        queryFn: ({ pageParam }) =>
            promotionsClient.listSalePromotionLog({
                promotionId,
                retailerId,
                limit: PAGE_SIZE,
                nextToken: pageParam,
            }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (last) => last.nextToken ?? undefined,
        enabled: isPromotionsConfigured(),
        retry: shouldRetryPromotionsQuery,
    });
    return {
        entries: query.data?.pages.flatMap((page) => page.items) ?? [],
        hasNextPage: query.hasNextPage,
        isFetchingNextPage: query.isFetchingNextPage,
        isPending: query.isPending,
        error: query.error,
        fetchNextPage: () => query.fetchNextPage(),
        refetch: () => query.refetch(),
    };
}

export function useChangeSalePromotionStatus() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            promotionId,
            transition,
            retailerId,
        }: {
            promotionId: string;
            transition: SalePromotionTransition;
            retailerId?: string;
        }) =>
            retailerId
                ? promotionsClient.changeSalePromotionStatus(
                      promotionId,
                      { transition },
                      { retailerId },
                  )
                : promotionsClient.changeSalePromotionStatus(promotionId, { transition }),
        // Con éxito o con 409 la lista quedó vieja: el cambio también deja evento en el historial.
        onSettled: () => {
            void qc.invalidateQueries({ queryKey: KEY });
        },
    });
}

export function useExportSalePromotionLogCsv() {
    return useMutation({
        mutationFn: ({ promotionId, retailerId }: { promotionId?: string; retailerId?: string }) =>
            promotionsClient.exportSalePromotionLogCsv({ promotionId, retailerId }),
    });
}
